import {CompleteCallback, sound as Sound} from '@pixi/sound';
import {SoundDuckConfig} from '@gongxigames/framework/slot/types';
import {
    consoleLogger,
    CoreDI,
    CoreSettings,
    Device,
    IBrowser,
    IDuckMusicOptions,
    IEventManagerWithAsync,
    inject,
    injectable,
    IService,
    ISoundPlayer,
    SystemComponentType,
    Tween,
    UpdateManager
} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {SoundsDuckingConfig} from '@gongxigames/framework/slot/fragments/SoundsDuckingConfig';

@injectable()
export class SoundPlayer implements ISoundPlayer, IService {
    public static NAME: symbol = CoreDI.SoundPlayer;
    public type: SystemComponentType = 'service';

    public static readonly UNDUCK_MUSIC: symbol = Symbol('UNDUCK_MUSIC');

    @inject(DI.SoundManager)
    protected _soundManager!: SoundManager;

    @inject(CoreDI.UpdateManager)
    protected _updateManager!: UpdateManager;

    @inject(CoreDI.Settings)
    protected _settings!: CoreSettings;

    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync<GlobalEventsMapForBackwardCompatibility>;

    @inject(CoreDI.Device)
    protected _device!: Device;

    protected _isMusicPlaying = false;
    protected _currentMusicID = '';

    protected _currentDuckingTween?: Tween;
    protected duckSoundReasons: string[] = [];

    public init(): void {
        this.setupEvents();

        if (this._device.iOS) {
            this._browser.addEventListener('focus', this.onFocused.bind(this));
            this.resumeAudioAfterInteractionForIOS = this.resumeAudioAfterInteractionForIOS.bind(this);
        }
    }

    protected setupEvents(): void {
        this._eventManager.on('General.Screen.BECAME_VISIBLE', this.onBecameVisible.bind(this), this);
        this._eventManager.on('General.Screen.BECAME_INVISIBLE', this.onBecameInvisible.bind(this), this);
        // Unduck background music in specific cases
        this._eventManager.on(SoundPlayer.UNDUCK_MUSIC, this.unduckMusic.bind(this), this);
        this._eventManager.on('General.Game.STARTED', this.onGameStarted.bind(this), this);
        this._eventManager.on('General.ResourcesLoading.LOADED_ALL_SOUNDS', () => {
            this._eventManager.off('General.Game.STARTED', this);
        }, this);
    }

    private onGameStarted(): void {
        this._eventManager.on('General.ResourcesLoading.LOADED_ALL_SOUNDS', this.playMusic.bind(this, 'maingame'), this);
    }

    public play(soundID: string, loop = false, onCompleteCallback: any = null, stopIfPlaying = true): void {
        const sound = this.soundManager.getSound(soundID);

        if (!sound) {
            consoleLogger.error(`There's no sound: '${soundID}'`);

            return;
        }

        this.soundManager.setSoundProperties(sound, soundID);

        if (stopIfPlaying && sound.isPlaying) {
            sound.stop();
        }

        // TODO: onDuckingSndCompleted overrides onCompleteCallback

        const duckConfig = SoundsDuckingConfig.getDuckingConfig(soundID);

        if (duckConfig) {
            this.duckMusic(duckConfig);
            this.duckSoundReasons.push(soundID);
            sound.play({loop, complete: this.onDuckingSndCompleted.bind(this, soundID)});
        } else if (onCompleteCallback) {
            sound.play({loop, complete: onCompleteCallback});
        } else {
            sound.play({loop});
        }
    }

    protected async onBecameVisible(): Promise<void> {
        if (this._device.iPhone || this._device.iPad) {
            this.resumeAudioContextIfClosed();
            document.addEventListener('touchend', this.resumeAudioAfterInteractionForIOS);
        }

        if (this._device.firefox) {
            await Sound.context.audioContext.resume();
        }

        Sound.resumeAll();
    }

    protected async resumeAudioAfterInteractionForIOS(): Promise<void> {
        await Sound.context.audioContext.resume();
        document.removeEventListener('touchend', this.resumeAudioAfterInteractionForIOS);
    }

    protected onBecameInvisible(): void {
        Sound.pauseAll();
    }

    protected removeDuckingSoundReason(soundID: string): void {
        const duckSoundReasonIndex = this.duckSoundReasons.indexOf(soundID);

        if (duckSoundReasonIndex >= 0) {
            this.duckSoundReasons.splice(duckSoundReasonIndex, 1);
        }
    }

    protected onDuckingSndCompleted(soundID: string): void {
        this.removeDuckingSoundReason(soundID);

        if (!this._currentDuckingTween || !this._currentDuckingTween.isPlaying) {
            this.unduckMusicIfNoBlockingReasons();
        }
    }

    protected unduckMusicIfNoBlockingReasons(): void {
        if (this.duckSoundReasons.length === 0) {
            this.unduckMusic();
        }
    }

    public stop(soundID: string): void {
        const sound = this.soundManager.getSound(soundID);

        if (!sound) {
            consoleLogger.error(`There's no sound: '${soundID}'`);

            return;
        }

        this.soundManager.getSound(soundID).stop();

        if (SoundsDuckingConfig.hasDuckingFor(soundID)) {
            this.removeDuckingSoundReason(soundID);
            this.unduckMusicIfNoBlockingReasons();
        }
    }

    public playMusic(musicID: string, complete?: CompleteCallback): void {
        this.stopMusic();

        if (this.soundManager.hasMusic(musicID)) {
            const {loop = true} = this.soundManager.soundsProperties[musicID] || {};

            this.soundManager.setSoundProperties(this.soundManager.getMusic(musicID), musicID);
            this.soundManager.getMusic(musicID).play({loop, complete});
            this._isMusicPlaying = true;
            this._currentMusicID = musicID;
        }
    }

    public stopMusic(): void {
        if (this._isMusicPlaying) {
            this.restoreVolume();
            this.soundManager.getMusic(this._currentMusicID).stop();
            this._isMusicPlaying = false;
        }
    }

    public isMusicPlaying(): boolean {
        return this._isMusicPlaying;
    }

    public getPlayingMusicID(): string | null {
        return this.isMusicPlaying() ? this._currentMusicID : null;
    }

    public duckMusic({volume = 0.1, fadeDuration = 500}: IDuckMusicOptions = {}): void {
        this.moveMusicVolumeTo({volume, fadeDuration});
    }

    public unduckMusic(): void {
        const {volume, duck} = this.soundManager.getConfigByID(this._currentMusicID) || {};

        this.duckMusic({volume: volume || 1, fadeDuration: duck?.fadeDuration || 500});
    }

    protected moveMusicVolumeTo(data: SoundDuckConfig): void {
        const music = this.soundManager.getMusic(this._currentMusicID);

        this.killCurrentDuckingTween();

        if (music) {
            this._currentDuckingTween = new Tween(music)
                .to({volume: data.volume}, data.fadeDuration)
                .start();

            this._updateManager.add(this._currentDuckingTween);
        }
    }

    protected restoreVolume(value?: number): void {
        const config = this.soundManager.getConfigByID(this._currentMusicID);

        if (value === undefined && config && config.volume !== undefined) {
            value = config.volume;
        }

        this.killCurrentDuckingTween();

        if (value !== undefined && this.soundManager.getMusic(this._currentMusicID)) {
            this.soundManager.getMusic(this._currentMusicID).volume = value;
        }
    }

    protected killCurrentDuckingTween(): void {
        if (this._currentDuckingTween && this._currentDuckingTween.isPlaying) {
            this._currentDuckingTween.stop();
            this._updateManager.remove(this._currentDuckingTween);
        }
    }

    public get soundManager(): SoundManager {
        return this._soundManager;
    }

    private onFocused(): void {
        this.resumeAudioContextIfClosed();
    }

    private async resumeAudioContextIfClosed(): Promise<void> {
        const context = Sound.context.audioContext;
        const isAudioContextClosed =
            context.state === 'closed' ||
            context.state === 'suspended' ||
            context.state as string === 'interrupted';

        if (isAudioContextClosed) {
            await context.suspend();
            await context.resume();
        }
    }

    public destroy(): void {
        if (this._currentMusicID && this.soundManager.getMusic(this._currentMusicID)) {
            this.soundManager.getMusic(this._currentMusicID).stop();
        }

        this.killCurrentDuckingTween();
        this._eventManager.off('General.Screen.BECAME_VISIBLE', this);
        this._eventManager.off('General.Screen.BECAME_INVISIBLE', this);
        this._eventManager.off(SoundPlayer.UNDUCK_MUSIC, this);
        this._eventManager.off('General.ResourcesLoading.LOADED_ALL_SOUNDS', this);
    }
}
